import { Features, ViewIds, type Feature } from '@ardoq/api-types';
import { MetaInfo, MetaInfoViewStatus } from 'streams/views/mainContent/types';
import { getViewThumbnailSrc } from 'tabview/consts';

export const viewIdToUserFriendlyName: Record<ViewIds, string> = {
  [ViewIds.PAGESVIEW]: 'Pages',
  [ViewIds.BLOCK_DIAGRAM]: 'Block Diagram',
  [ViewIds.DEPENDENCY_MAP_2]: 'Dependency Map',
  [ViewIds.RELATIONSHIPS_3]: 'Relationships',
  [ViewIds.NETWORK]: 'Network',
  [ViewIds.TABLEVIEW]: 'Tables',
  [ViewIds.DEPMATRIX]: 'Dependency Matrix',
  [ViewIds.CAPABILITY_MAP]: 'Capability Map',
  [ViewIds.SWIMLANE]: 'Swimlanes',
  [ViewIds.COMPONENTTREE]: 'Component tree',
  [ViewIds.DEPWHEEL]: 'Dependency Wheel',
  [ViewIds.TREEMAP]: 'Treemap',
  [ViewIds.TAGSCAPE]: 'Tagscape',
  [ViewIds.REFERENCETABLE]: 'Reference Table',
  [ViewIds.TIMELINE]: 'Timeline',
  [ViewIds.BUBBLE]: 'Bubble Chart',
  [ViewIds.ROADMAP]: 'Roadmap view',
  [ViewIds.COMPONENT_MATRIX]: 'Component Matrix',
  [ViewIds.COMPONENT_MATRIX_2]: 'Component Matrix',
  [ViewIds.SPIDER]: 'Spider Chart',
  [ViewIds.CIRCULAR_RELATIONSHIP_DIAGRAM]: 'Explorer View',
  [ViewIds.HIERARCHICAL_TREEMAP]: 'Hierarchical Treemap View',
  [ViewIds.HIERARCHICAL_PACKING]: 'Hierarchical Circle Packing View',
  [ViewIds.SUNBURST]: 'Sunburst View',
  [ViewIds.METAMODEL]: 'Meta Model',
  [ViewIds.ISOMETRIC_BLOCK_DIAGRAM]: 'Isometric Block Diagram',
  [ViewIds.SEQUENCE]: 'Sequence diagram',
  [ViewIds.REFERENCE_AGGREGATION_VIEW]: 'Aggregation',
  [ViewIds.PROCESSFLOW]: 'Process flow',
  [ViewIds.HEATMAP]: 'Heatmap',
  [ViewIds.API_DOCUMENTATION]: 'API Documentation',
  [ViewIds.RELATIONSHIPS]: 'Relationships',
  [ViewIds.DEPMAP]: 'Dependency Map',
  [ViewIds.NONE]: 'None',
  [ViewIds.SIMPLE_BLOCK_DIAGRAM]: 'Simple block diagram',
  [ViewIds.BLOCKS]: 'Interactive Blocks',
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: 'Block Diagram',
  [ViewIds.ASSISTANT_DIAGRAM]: 'Assistant Diagram',
  [ViewIds.ASSISTANT_BLOCKS]: 'Assistant Blocks',
  [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: 'Flexible Block Diagram',
  [ViewIds.SURFACE_3D]: '3D Surface Chart',
  [ViewIds.CONTOUR]: 'Contour Chart',
  [ViewIds.SCATTER_3D]: '3D Scatter Chart',
  [ViewIds.BUBBLE_3D]: '3D Bubble Chart',
  [ViewIds.NETWORK_3D]: '3D Network',
  [ViewIds.SPIDER_2]: 'Spider Chart',
  [ViewIds.POLAR]: 'Polar Chart',
};

export const viewIdToTranslationKey: Record<ViewIds, string> = {
  [ViewIds.PAGESVIEW]: 'views:viewName.pagesView',
  [ViewIds.BLOCK_DIAGRAM]: 'views:viewName.blockDiagram',
  [ViewIds.DEPENDENCY_MAP_2]: 'views:viewName.dependencyMap2',
  [ViewIds.RELATIONSHIPS_3]: 'views:viewName.relationships3',
  [ViewIds.NETWORK]: 'views:viewName.network',
  [ViewIds.TABLEVIEW]: 'views:viewName.tableView',
  [ViewIds.DEPMATRIX]: 'views:viewName.depMatrix',
  [ViewIds.CAPABILITY_MAP]: 'views:viewName.capabilityMap',
  [ViewIds.SWIMLANE]: 'views:viewName.swimlane',
  [ViewIds.COMPONENTTREE]: 'views:viewName.componentTree',
  [ViewIds.DEPWHEEL]: 'views:viewName.depWheel',
  [ViewIds.TREEMAP]: 'views:viewName.treemap',
  [ViewIds.TAGSCAPE]: 'views:viewName.tagscape',
  [ViewIds.REFERENCETABLE]: 'views:viewName.referenceTable',
  [ViewIds.TIMELINE]: 'views:viewName.timeline',
  [ViewIds.BUBBLE]: 'views:viewName.bubble',
  [ViewIds.ROADMAP]: 'views:viewName.roadmap',
  [ViewIds.COMPONENT_MATRIX]: 'views:viewName.componentMatrix',
  [ViewIds.COMPONENT_MATRIX_2]: 'views:viewName.componentMatrix2',
  [ViewIds.SPIDER]: 'views:viewName.spider',
  [ViewIds.CIRCULAR_RELATIONSHIP_DIAGRAM]:
    'views:viewName.circularRelationshipDiagram',
  [ViewIds.HIERARCHICAL_TREEMAP]: 'views:viewName.hierarchicalTreemap',
  [ViewIds.HIERARCHICAL_PACKING]: 'views:viewName.hierarchicalPacking',
  [ViewIds.SUNBURST]: 'views:viewName.sunburst',
  [ViewIds.METAMODEL]: 'views:viewName.metamodel',
  [ViewIds.ISOMETRIC_BLOCK_DIAGRAM]: 'views:viewName.isometricBlockDiagram',
  [ViewIds.SEQUENCE]: 'views:viewName.sequence',
  [ViewIds.REFERENCE_AGGREGATION_VIEW]: 'views:viewName.referenceAggregation',
  [ViewIds.PROCESSFLOW]: 'views:viewName.processFlow',
  [ViewIds.HEATMAP]: 'views:viewName.heatmap',
  [ViewIds.API_DOCUMENTATION]: 'views:viewName.apiDocumentation',
  [ViewIds.RELATIONSHIPS]: 'views:viewName.relationships',
  [ViewIds.DEPMAP]: 'views:viewName.depMap',
  [ViewIds.NONE]: 'views:viewName.none',
  [ViewIds.SIMPLE_BLOCK_DIAGRAM]: 'views:viewName.simpleBlockDiagram',
  [ViewIds.BLOCKS]: 'views:viewName.blocks',
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: 'views:viewName.modernizedBlockDiagram',
  [ViewIds.ASSISTANT_DIAGRAM]: 'views:viewName.assistantDiagram',
  [ViewIds.ASSISTANT_BLOCKS]: 'views:viewName.assistantBlocks',
  [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: 'views:viewName.flexibleBlockDiagram',
  [ViewIds.SURFACE_3D]: 'views:viewName.surface3d',
  [ViewIds.CONTOUR]: 'views:viewName.contour',
  [ViewIds.SCATTER_3D]: 'views:viewName.scatter3d',
  [ViewIds.BUBBLE_3D]: 'views:viewName.bubble3d',
  [ViewIds.NETWORK_3D]: 'views:viewName.network3d',
  [ViewIds.SPIDER_2]: 'views:viewName.spider2',
  [ViewIds.POLAR]: 'views:viewName.polar',
};

export type ViewpointModeViewDefinition = ViewDefinition & {
  fullMultiLabelSupport: boolean;
  fullGroupingSupport: boolean;
};

export type ViewDefinition = MetaInfo & {
  /** Any one of these features enables the view; none listed enables it always. */
  availabilityFeatures: readonly Feature[];
  isQuickStartView: boolean;
  /** Discontinued unless any one of these features is enabled. */
  discontinuedUnlessFeatures?: readonly Feature[];
  /** In presentation mode, `availabilityFeatures` and `discontinuedUnlessFeatures` don't apply. */
  ignoresFeaturesInPresentations?: boolean;
};

const DEPENDENCY_MAP_2_VIEW = {
  id: ViewIds.DEPENDENCY_MAP_2,
  name: viewIdToUserFriendlyName[ViewIds.DEPENDENCY_MAP_2],
  description: 'Dependency Map',
  exampleImage: getViewThumbnailSrc(ViewIds.DEPENDENCY_MAP_2),
  features: { hasLegend: true },
  availabilityFeatures: [],
  isQuickStartView: true,
} as const satisfies ViewDefinition;

const RELATIONSHIPS_3_VIEW = {
  id: ViewIds.RELATIONSHIPS_3,
  name: viewIdToUserFriendlyName[ViewIds.RELATIONSHIPS_3],
  description:
    'Shows how the components are linked to each other and what kind of link types they have between them. You can include the parent components via the option menu. Useful for exploring and identifying dependencies.',
  exampleImage: getViewThumbnailSrc(ViewIds.RELATIONSHIPS_3),
  features: { hasLegend: true },
  availabilityFeatures: [],
  isQuickStartView: true,
} as const satisfies ViewDefinition;

const TIMELINE_VIEW = {
  id: ViewIds.TIMELINE,
  name: viewIdToUserFriendlyName[ViewIds.TIMELINE],
  description: 'Displays timespan data for components.',
  exampleImage: getViewThumbnailSrc(ViewIds.TIMELINE),
  features: { hasLegend: true },
  availabilityFeatures: [],
  isQuickStartView: true,
} as const satisfies ViewDefinition;

const BUBBLE_VIEW = {
  id: ViewIds.BUBBLE,
  name: viewIdToUserFriendlyName[ViewIds.BUBBLE],
  description: `A bubble chart is useful for visualizing the relationships between the numeric field values of multiple components.  The components are positioned and sized according to the values of three numeric fields, designated as the X, Y, and Size fields.`,
  exampleImage: getViewThumbnailSrc(ViewIds.BUBBLE),
  features: { hasLegend: true },
  availabilityFeatures: [],
  isQuickStartView: true,
} as const satisfies ViewDefinition;

/**
 * Discontinued views can still be loaded from saved slides. They need their
 * meta info so that such a slide is known to show a discontinued view rather
 * than a view that is simply missing. They remain workspace-mode settings keys.
 */
export const DISCONTINUED_VIEW_DEFINITIONS = [
  {
    id: ViewIds.CIRCULAR_RELATIONSHIP_DIAGRAM,
    name: viewIdToUserFriendlyName[ViewIds.CIRCULAR_RELATIONSHIP_DIAGRAM],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.SEQUENCE,
    name: viewIdToUserFriendlyName[ViewIds.SEQUENCE],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.REFERENCE_AGGREGATION_VIEW,
    name: viewIdToUserFriendlyName[ViewIds.REFERENCE_AGGREGATION_VIEW],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.PROCESSFLOW,
    name: viewIdToUserFriendlyName[ViewIds.PROCESSFLOW],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.HEATMAP,
    name: viewIdToUserFriendlyName[ViewIds.HEATMAP],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.API_DOCUMENTATION,
    name: viewIdToUserFriendlyName[ViewIds.API_DOCUMENTATION],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.RELATIONSHIPS,
    name: viewIdToUserFriendlyName[ViewIds.RELATIONSHIPS],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    deprecationSuccessor: ViewIds.RELATIONSHIPS_3,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.DEPMAP,
    name: viewIdToUserFriendlyName[ViewIds.DEPMAP],
    viewStatus: MetaInfoViewStatus.DISCONTINUED,
    deprecationSuccessor: ViewIds.DEPENDENCY_MAP_2,
    availabilityFeatures: [],
    isQuickStartView: false,
  },
] as const satisfies readonly ViewDefinition[];

export const WORKSPACE_MODE_VIEW_DEFINITIONS = [
  {
    id: ViewIds.PAGESVIEW,
    name: viewIdToUserFriendlyName[ViewIds.PAGESVIEW],
    description:
      'Shows detailed information about fields and descriptions on components and references',
    exampleImage: getViewThumbnailSrc(ViewIds.PAGESVIEW),
    features: { hasLegend: true },
    isHiddenInViewSelect: true,
    availabilityFeatures: [],
    isQuickStartView: true,
  },
  {
    id: ViewIds.BLOCK_DIAGRAM,
    name: viewIdToUserFriendlyName[ViewIds.BLOCK_DIAGRAM],
    description: 'Shows linked components as block diagram.',
    exampleImage: getViewThumbnailSrc(ViewIds.BLOCK_DIAGRAM),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: true,
  },
  DEPENDENCY_MAP_2_VIEW,
  RELATIONSHIPS_3_VIEW,
  {
    id: ViewIds.NETWORK,
    name: viewIdToUserFriendlyName[ViewIds.NETWORK],
    description:
      'Shows how the components are linked to each other and what kind of link types they have between them. You can include the parent components via the option menu. Useful for exploring and identifying dependencies.',
    exampleImage: getViewThumbnailSrc(ViewIds.NETWORK),
    features: { hasLegend: true },
    availabilityFeatures: [Features.SHOW_OLD_RELATIONSHIP_VIEW_2],
    isQuickStartView: false,
    ignoresFeaturesInPresentations: true,
  },
  {
    id: ViewIds.TABLEVIEW,
    name: viewIdToUserFriendlyName[ViewIds.TABLEVIEW],
    description: 'Shows all your components in a consumable table view.',
    exampleImage: getViewThumbnailSrc(ViewIds.TABLEVIEW),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: true,
  },
  {
    id: ViewIds.DEPMATRIX,
    name: viewIdToUserFriendlyName[ViewIds.DEPMATRIX],
    description: 'Dependency Matrix',
    exampleImage: getViewThumbnailSrc(ViewIds.DEPMATRIX),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: true,
  },
  {
    id: ViewIds.CAPABILITY_MAP,
    name: viewIdToUserFriendlyName[ViewIds.CAPABILITY_MAP],
    description: 'Shows Capabilities.',
    exampleImage: getViewThumbnailSrc(ViewIds.CAPABILITY_MAP),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.SWIMLANE,
    name: viewIdToUserFriendlyName[ViewIds.SWIMLANE],
    description:
      'Shows how the components interact with each other categorized by swimlanes (grouped by their parents).',
    exampleImage: getViewThumbnailSrc(ViewIds.SWIMLANE),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.COMPONENTTREE,
    name: viewIdToUserFriendlyName[ViewIds.COMPONENTTREE],
    description:
      'Shows the components as a Table of Contents-like tree. The tree can be collapsed and expanded.',
    exampleImage: getViewThumbnailSrc(ViewIds.COMPONENTTREE),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.DEPWHEEL,
    name: viewIdToUserFriendlyName[ViewIds.DEPWHEEL],
    description:
      'Dependency wheel shows you how your components are connected in a circular view.',
    exampleImage: getViewThumbnailSrc(ViewIds.DEPWHEEL),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.TREEMAP,
    name: viewIdToUserFriendlyName[ViewIds.TREEMAP],
    description:
      'Treemap is a zoomable system map that shows how important a component is, by making a component bigger or smaller. The importance is calculated by the number of child components or incoming references.',
    exampleImage: getViewThumbnailSrc(ViewIds.TREEMAP),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.TAGSCAPE,
    name: viewIdToUserFriendlyName[ViewIds.TAGSCAPE],
    description:
      'Shows all tagged workspace components and references grouped by tags.',
    exampleImage: getViewThumbnailSrc(ViewIds.TAGSCAPE),
    features: { hasLegend: false },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.REFERENCETABLE,
    name: viewIdToUserFriendlyName[ViewIds.REFERENCETABLE],
    description: 'Reference Table',
    exampleImage: getViewThumbnailSrc(ViewIds.REFERENCETABLE),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  TIMELINE_VIEW,
  BUBBLE_VIEW,
  {
    id: ViewIds.ROADMAP,
    name: viewIdToUserFriendlyName[ViewIds.ROADMAP],
    description: 'Roadmap',
    exampleImage: getViewThumbnailSrc(ViewIds.ROADMAP),
    features: { hasLegend: false },
    deprecationSuccessor: ViewIds.COMPONENT_MATRIX,
    availabilityFeatures: [],
    isQuickStartView: false,
    discontinuedUnlessFeatures: [Features.ROADMAP_FOR_HMCTS],
    ignoresFeaturesInPresentations: true,
  },
  {
    id: ViewIds.COMPONENT_MATRIX,
    name: viewIdToUserFriendlyName[ViewIds.COMPONENT_MATRIX],
    description: `Displays components in a grid, grouped by directly referenced components or list field values.`,
    exampleImage: getViewThumbnailSrc(ViewIds.COMPONENT_MATRIX),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: true,
  },
  {
    id: ViewIds.SPIDER,
    name: viewIdToUserFriendlyName[ViewIds.SPIDER],
    description: 'Spider Chart View',
    exampleImage: getViewThumbnailSrc(ViewIds.SPIDER),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: false,
  },
  {
    id: ViewIds.ISOMETRIC_BLOCK_DIAGRAM,
    name: viewIdToUserFriendlyName[ViewIds.ISOMETRIC_BLOCK_DIAGRAM],
    description: 'Shows linked components as block diagram.',
    exampleImage: getViewThumbnailSrc(ViewIds.BLOCK_DIAGRAM),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.ISOMETRIC_BLOCK_DIAGRAM],
    isQuickStartView: false,
    ignoresFeaturesInPresentations: true,
  },
  ...DISCONTINUED_VIEW_DEFINITIONS,
] as const satisfies readonly ViewDefinition[];

/** Also the order of the viewpoint-mode view picker. */
export const VIEWPOINT_MODE_VIEW_DEFINITIONS = [
  {
    id: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
    name: viewIdToUserFriendlyName[ViewIds.MODERNIZED_BLOCK_DIAGRAM],
    description: 'Shows linked components as block diagram.',
    exampleImage: getViewThumbnailSrc(ViewIds.BLOCK_DIAGRAM),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: true,
    fullMultiLabelSupport: true,
    fullGroupingSupport: true,
  },
  {
    ...DEPENDENCY_MAP_2_VIEW,
    fullMultiLabelSupport: true,
    fullGroupingSupport: true,
  },
  {
    ...RELATIONSHIPS_3_VIEW,
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    ...TIMELINE_VIEW,
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    ...BUBBLE_VIEW,
    fullMultiLabelSupport: false,
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.COMPONENT_MATRIX_2,
    name: viewIdToUserFriendlyName[ViewIds.COMPONENT_MATRIX_2],
    description: `Displays components in a grid, grouped by directly referenced components or list field values.`,
    exampleImage: getViewThumbnailSrc(ViewIds.COMPONENT_MATRIX_2),
    features: { hasLegend: true },
    availabilityFeatures: [],
    isQuickStartView: true,
    fullMultiLabelSupport: true,
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.BLOCKS,
    name: viewIdToUserFriendlyName[ViewIds.BLOCKS],
    description: 'Shows linked components as block diagram.',
    exampleImage: getViewThumbnailSrc(ViewIds.BLOCK_DIAGRAM),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [],
    isQuickStartView: true,
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.ASSISTANT_DIAGRAM,
    name: viewIdToUserFriendlyName[ViewIds.ASSISTANT_DIAGRAM],
    description: 'AI-generated diagram of the current view.',
    exampleImage: getViewThumbnailSrc(ViewIds.ASSISTANT_DIAGRAM),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.ASSISTANT_DIAGRAM],
    isQuickStartView: false,
    fullMultiLabelSupport: true,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.ASSISTANT_BLOCKS,
    name: viewIdToUserFriendlyName[ViewIds.ASSISTANT_BLOCKS],
    description: 'AI-positioned block layout of the current view.',
    exampleImage: getViewThumbnailSrc(ViewIds.ASSISTANT_BLOCKS),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.ASSISTANT_BLOCKS_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: true,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.FLEXIBLE_BLOCK_DIAGRAM,
    name: viewIdToUserFriendlyName[ViewIds.FLEXIBLE_BLOCK_DIAGRAM],
    description: 'Shows linked components as block diagram.',
    exampleImage: getViewThumbnailSrc(ViewIds.BLOCK_DIAGRAM),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [
      Features.FLEXIBLE_BLOCK_DIAGRAM,
      Features.FLEXIBLE_BLOCK_DIAGRAM_AS_BLOCK_DIAGRAM,
    ],
    isQuickStartView: true,
    fullMultiLabelSupport: true,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.HIERARCHICAL_TREEMAP,
    name: viewIdToUserFriendlyName[ViewIds.HIERARCHICAL_TREEMAP],
    description: 'Display components by weight',
    exampleImage: getViewThumbnailSrc(ViewIds.TREEMAP),
    features: { hasLegend: false },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.HIERARCHICAL_TREEMAP],
    isQuickStartView: false,
    /* These three take `getGraphNodeOrGroupLabelForViewpointMode(...).mainLabel` and drop the other
       labels - a tile, a circle and a ring have no room beneath a name - and draw no references at
       all. Unlike the bubble chart, grouping is what they are for. */
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.HIERARCHICAL_PACKING,
    name: viewIdToUserFriendlyName[ViewIds.HIERARCHICAL_PACKING],
    description: 'Display components by weight',
    exampleImage: getViewThumbnailSrc(ViewIds.TREEMAP),
    features: { hasLegend: false },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.HIERARCHICAL_TREEMAP],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.SUNBURST,
    name: viewIdToUserFriendlyName[ViewIds.SUNBURST],
    description: 'Display components by weight',
    exampleImage: getViewThumbnailSrc(ViewIds.TREEMAP),
    features: { hasLegend: false },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.HIERARCHICAL_TREEMAP],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.SURFACE_3D,
    name: viewIdToUserFriendlyName[ViewIds.SURFACE_3D],
    description: `Plots a third numeric field as relief over the plane formed by two others, so a whole estate reads as a landscape. Where no component sits, height is interpolated between the ones that do; the chart says nothing outside the area they cover.`,
    exampleImage: getViewThumbnailSrc(ViewIds.SURFACE_3D),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.SURFACE_3D_VIEW],
    isQuickStartView: false,
    /* Builds its labels with `getLabelWithFormattingForViewsNotSupportingMultilabeling` and passes an
       empty `referenceLabelFilters`, so a second label rule and any reference label formatting are
       dropped. Same as the bubble chart, which lacks both supports for the same two reasons. */
    fullMultiLabelSupport: false,
    /* Plots one flat mark per component out of `scopeComponentIds` and never groups. */
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.CONTOUR,
    name: viewIdToUserFriendlyName[ViewIds.CONTOUR],
    description: `Reads a numeric field as a map: components are placed by two other fields and the third becomes color, with lines joining equal values. The same gridding as the 3D surface, read from directly above, which is easier to compare across and harder to hide behind a peak.`,
    exampleImage: getViewThumbnailSrc(ViewIds.CONTOUR),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.CONTOUR_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.SCATTER_3D,
    name: viewIdToUserFriendlyName[ViewIds.SCATTER_3D],
    description: `Places each component in space by three numeric fields and draws it as a mark, with a line down to the base so its height can be read. Nothing is interpolated: every mark is a component, and the gaps between them are gaps.`,
    exampleImage: getViewThumbnailSrc(ViewIds.SCATTER_3D),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.SCATTER_3D_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.BUBBLE_3D,
    name: viewIdToUserFriendlyName[ViewIds.BUBBLE_3D],
    description: `The 3D scatter with a fourth field varying mark size, so a component's position, height and magnitude are all readable at once. Size is mapped by area rather than radius, which is what keeps a large value from looking several times larger than it is.`,
    exampleImage: getViewThumbnailSrc(ViewIds.BUBBLE_3D),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.BUBBLE_3D_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.NETWORK_3D,
    name: viewIdToUserFriendlyName[ViewIds.NETWORK_3D],
    description: `Components and their references in three dimensions, with five layouts over the same data: a force-directed cloud for finding clusters, planes stacked by component type for reading the direction of dependencies, a radial tree for the hierarchy, hierarchical edge bundling for seeing which parts of the estate are coupled, and nested spheres when a grouping rule is set. Component size can stand for the number of references or for any numeric field in the viewpoint; hovering a component lights its neighborhood and dims the rest.`,
    exampleImage: getViewThumbnailSrc(ViewIds.NETWORK_3D),
    features: { hasLegend: true },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.NETWORK_3D_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: true,
  },
  {
    id: ViewIds.SPIDER_2,
    name: viewIdToUserFriendlyName[ViewIds.SPIDER_2],
    description: `Compares components across several numeric fields at once, with one spoke per component and one shape per field. Draw the shapes as lines or filled areas, straight or smoothed.`,
    exampleImage: getViewThumbnailSrc(ViewIds.SPIDER_2),
    features: { hasLegend: false },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.SPIDER_2_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: false,
  },
  {
    id: ViewIds.POLAR,
    name: viewIdToUserFriendlyName[ViewIds.POLAR],
    description: `Plots each component by two numeric fields: one sets its angle around the circle and the other its distance from the center. Split the points into series by a list, text or checkbox field. Draw the series as lines or filled areas, straight or smoothed.`,
    exampleImage: getViewThumbnailSrc(ViewIds.POLAR),
    features: { hasLegend: false },
    viewStatus: MetaInfoViewStatus.BETA,
    availabilityFeatures: [Features.POLAR_VIEW],
    isQuickStartView: false,
    fullMultiLabelSupport: false,
    fullGroupingSupport: false,
  },
] as const satisfies readonly ViewpointModeViewDefinition[];
